import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { profileAction } from "@redux/modules";
import showErrMessage from "@components/ShowErrMessage";
import "./index.scss";
import {
  FaFileInvoice,
  FaStore,
  FaCalendarAlt,
  FaIdCard,
  FaReceipt,
  FaShoppingCart,
  FaBoxOpen,
  FaTimes,
} from 'react-icons/fa';

const STATUS_BADGES = {
  PROCESSING: { color: "processing", text: "İşleniyor" },
  VALIDATED: { color: "validated", text: "Onaylandı" },
  REJECTED: { color: "rejected", text: "Reddedildi" },
};

const ReceiptList = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const receiptList = useSelector((state) => state.profile.receiptList || []);

  useEffect(() => {
    loadReceipts();
  }, []);

  const loadReceipts = async () => {
    try {
      setIsLoading(true);
      await dispatch(profileAction.getReceiptList());
    } catch (err) {
      showErrMessage("Fişler yüklenirken bir hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewReceipt = (receipt) => {
    setSelectedReceipt(receipt);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedReceipt(null);
  };

  return (
    <div className="container-fluid py-4">
      <h2 className="mb-4">Yüklenen Fiş ve Faturalar</h2>
      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
        </div>
      ) : receiptList.length === 0 ? (
        <div className="text-center py-5">
          <i className="fa fa-file-invoice-dollar fa-3x text-muted mb-3"></i>
          <h5 className="text-muted">Fiş bulunamadı</h5>
        </div>
      ) : (
        <div className="row g-4">
          {receiptList.map((receipt) => (
            <div key={receipt.id} className="col-12 col-md-6 col-lg-6">
              <div className="card shadow-sm h-100 d-flex flex-column">
                <div className="card-body d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="flex-grow-1 me-3">
                      <h5
                        className="card-title mb-1 text-truncate"
                        title={receipt.issuerName || "İsimsiz Mağaza"}
                      >
                        {receipt.issuerName || "İsimsiz Mağaza"}
                      </h5>
                      <p className="text-muted small mb-0">
                        {new Date(receipt.uploadDateTime).toLocaleDateString(
                          "tr-TR"
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-2 align-items-center">
                      <span className="text-muted">Durum:</span>
                      <span
                        className={`badge bg-${STATUS_BADGES[receipt.invoiceStatus]?.color || "secondary"} py-2 px-3`}
                      >
                        {STATUS_BADGES[receipt.invoiceStatus]?.text ||
                          receipt.invoiceStatus ||
                          "Bilinmiyor"}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Fiş No:</span>
                      <span
                        className="text-truncate ms-2"
                        style={{ maxWidth: "150px" }}
                        title={receipt.id}
                      >
                        {receipt.id}
                      </span>
                    </div>
                    {receipt.invoiceProducts && (
                      <div className="text-muted small">
                        {receipt.invoiceProducts.length} ürün
                      </div>
                    )}
                  </div>
                  <div className="mt-auto pt-3 border-top">
                    <button
                      className="btn btn-outline-primary btn-sm w-100"
                      onClick={() => handleViewReceipt(receipt)}
                    >
                      <i className="fa fa-eye me-2"></i>Görüntüle
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detay Modalı */}
      {showModal && selectedReceipt && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            background: "rgba(0,0,0,0.5)",
            zIndex: 9999,
          }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h5 className="modal-title d-flex align-items-center">
                  <FaFileInvoice className="me-2" /> Fiş Detayı
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row g-4">
                  {/* Sol Kolon - Temel Bilgiler */}
                  <div className="col-md-6">
                    <div className="card border-0 bg-light">
                      <div className="card-body">
                        <h6 className="card-subtitle mb-3 text-primary">
                          <FaStore className="me-2" /> Mağaza Bilgileri
                        </h6>
                        <div className="mb-3">
                          <p className="mb-1 fw-bold fs-5">
                            {selectedReceipt.issuerName || "İsimsiz Mağaza"}
                          </p>
                          <p className="mb-1 text-muted">
                            <FaCalendarAlt className="me-2" />
                            {new Date(
                              selectedReceipt.uploadDateTime
                            ).toLocaleDateString("tr-TR")}
                          </p>
                          {selectedReceipt.issuerVkn && (
                            <p className="mb-0 text-muted">
                              <FaIdCard className="me-2" />
                              VKN: {selectedReceipt.issuerVkn}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="card border-0 bg-light mt-3">
                      <div className="card-body">
                        <h6 className="card-subtitle mb-3 text-primary">
                          <FaReceipt className="me-2" /> Fiş Bilgileri
                        </h6>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted">Fiş No:</span>
                          <span className="fw-bold">{selectedReceipt.id}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted">Toplam Tutar:</span>
                          <span className="fw-bold">
                            {selectedReceipt.totalAmount
                              ? `${selectedReceipt.totalAmount} TL`
                              : "Belirtilmemiş"}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Durum:</span>
                          <span
                            className={`badge bg-${STATUS_BADGES[selectedReceipt.invoiceStatus]?.color || "secondary"} py-2 px-3`}
                          >
                            {STATUS_BADGES[selectedReceipt.invoiceStatus]
                              ?.text ||
                              selectedReceipt.invoiceStatus ||
                              "Bilinmiyor"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sağ Kolon - Ürün Listesi */}
                  <div className="col-md-6">
                    {selectedReceipt.invoiceProducts &&
                    selectedReceipt.invoiceProducts.length > 0 ? (
                      <div className="card border-0 bg-light h-100">
                        <div className="card-body">
                          <h6 className="card-subtitle mb-3 text-primary">
                            <FaShoppingCart className="me-2" /> Ürün Listesi
                          </h6>
                          <div className="table-responsive">
                            <table className="table table-hover">
                              <thead>
                                <tr>
                                  <th>Ürün Adı</th>
                                  <th className="text-end">Adet</th>
                                  <th className="text-end">Fiyat</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedReceipt.invoiceProducts.map(
                                  (product, index) => (
                                    <tr key={index}>
                                      <td>{product.productName}</td>
                                      <td className="text-end">
                                        {product.quantity || 1}
                                      </td>
                                      <td className="text-end">
                                        {product.price
                                          ? `${product.price} TL`
                                          : "-"}
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="card border-0 bg-light h-100">
                        <div className="card-body d-flex align-items-center justify-content-center">
                          <div className="text-center text-muted">
                            <FaBoxOpen
                              className="mb-3"
                              style={{ fontSize: "2rem" }}
                            />
                            <p className="mb-0">Ürün bilgisi bulunamadı</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top">
                <button
                  type="button"
                  className="btn bg-rejected d-flex align-items-center"
                  onClick={handleCloseModal}
                >
                  <FaTimes className="me-2" /> Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptList;
